import { type FC, useEffect, useRef, useState } from "react";
import { useSubscription } from "@apollo/client";
import { Box, Paper } from "@mui/material";
import { useUser } from "@/shared/hooks/use-user/useUser";
import {
  ChatBanCreatedSubscription,
  ChatBanDeletedSubscription,
  ChatMembersUpdatedSubscription,
} from "@/entities/contents";
import type {
  ChatBanCreatedSubscriptionType,
  ChatBanDeletedSubscriptionType,
  ChatMembersUpdatedSubscriptionType,
} from "@/types/graphql/site.generated";
import { ChatHeaderWithForm, type ChatMention } from "./ChatHeaderWithForm";
import { ChatMessageList } from "./ChatMessageList";
import { ChatOnlineList } from "./ChatOnlineList";
import { ChatSoundSettings } from "./ChatSoundSettings";
import { useChatHeartbeat } from "./useChatHeartbeat";
import { useChatSoundNotifications } from "./useChatSoundNotifications";
import type { ChatMessageListItemType } from "./types";

export interface ContentChatOnlineMember {
  id: string;
  userId: string;
  userDisplayName?: string | null;
  userAvatar?: { url?: string | null } | null;
  isOnline: boolean;
}

export interface ContentChatRoomData {
  id: string;
  viewerIsModerator: boolean;
  viewerBan?: { id: string; expiredAt: string } | null;
  // Присутствие есть только у стрима: запрос видео его не спрашивает,
  // поэтому поля опциональны.
  onlineCount?: number;
  onlineMembers?: ContentChatOnlineMember[];
}

/**
 * stream: чат эфира, как был, с присутствием «сейчас смотрят», heartbeat
 * и звуком. video: комментарии под видео, только лента и форма, без
 * присутствия и звука, новые снизу, «показать ещё» вверх.
 */
export type ContentChatMode = "stream" | "video";

interface ContentChatProps {
  chatRoom: ContentChatRoomData;
  mode: ContentChatMode;
  variant: "sidebar" | "mobile" | "page";
}

export const ContentChat: FC<ContentChatProps> = ({
  chatRoom,
  mode,
  variant,
}) => {
  const { user } = useUser();
  const viewerId = user ? String(user.id) : null;
  const [replyTo, setReplyTo] = useState<ChatMessageListItemType | null>(null);
  // Упоминание живёт здесь, потому что ставится в ленте, а используется в
  // форме (референсное поведение: клик по аватарке, без кнопки у поля).
  const [mention, setMention] = useState<ChatMention | null>(null);
  const [onlineListOpen, setOnlineListOpen] = useState(false);
  const [soundAnchor, setSoundAnchor] = useState<HTMLElement | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [viewerBan, setViewerBan] = useState(chatRoom.viewerBan ?? null);
  const isStream = mode === "stream";
  const [onlineCount, setOnlineCount] = useState(chatRoom.onlineCount ?? 0);
  const [onlineMembers, setOnlineMembers] = useState(
    chatRoom.onlineMembers ?? [],
  );

  // «Держать низ ленты» живёт здесь, а не в самой ленте: отправляет
  // сообщение форма, а доскроллить должна лента. После успешной отправки
  // зритель обязан увидеть свой комментарий, даже если перед этим отлистал
  // ленту вверх и автоскролл был выключен.
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    setViewerBan(chatRoom.viewerBan ?? null);
  }, [chatRoom.viewerBan]);

  // Оба хука умеют не делать ничего при null (см. их сигнатуры), поэтому
  // режим video просто не передаёт им комнату: под видео нет ни «сейчас
  // смотрят», ни звуков.
  useChatHeartbeat(isStream ? chatRoom.id : null);
  useChatSoundNotifications(isStream ? chatRoom.id : null, viewerId);

  // Живое обновление бана: исходный chatRoom.viewerBan приходит только с
  // первой загрузкой страницы (StreamContentQuery) — без этих подписок
  // забаненный/разбаненный прямо во время эфира зритель увидел бы баннер
  // только после релоада (см. Task 16 Interfaces).
  useSubscription<ChatBanCreatedSubscriptionType>(ChatBanCreatedSubscription, {
    variables: { chatRoomId: chatRoom.id },
    onData: ({ data }) => {
      const ban = data.data?.chatBanCreated;
      if (ban && viewerId && ban.user.id === viewerId) {
        setViewerBan({ id: ban.id, expiredAt: ban.expiredAt });
      }
    },
  });
  // ChatBanDeletedPayload намеренно содержит только id удалённого бана (сам
  // ChatBan к моменту паблиша уже удалён из БД — см. комментарий на схеме
  // ChatBanDeletedPayload в chat.graphql), поэтому в отличие от created-ветки
  // тут нет user.id для сравнения — сверяем id бана с уже сохранённым
  // viewerBan.id.
  useSubscription<ChatBanDeletedSubscriptionType>(ChatBanDeletedSubscription, {
    variables: { chatRoomId: chatRoom.id },
    onData: ({ data }) => {
      const deletedBanId = data.data?.chatBanDeleted?.id;
      if (deletedBanId && viewerBan && deletedBanId === viewerBan.id) {
        setViewerBan(null);
      }
    },
  });

  // Presence живьём: chatRoom.onlineCount/onlineMembers приходят только один
  // раз со StreamContentQuery при загрузке страницы и без этой подписки
  // никогда не обновляются — счётчик онлайна и список участников замерли бы
  // навсегда (backend уже полностью реализует presence через
  // ChatMemberService/TTL-индекс, см. фикс-раунд ниже).
  useSubscription<ChatMembersUpdatedSubscriptionType>(
    ChatMembersUpdatedSubscription,
    {
      variables: { chatRoomId: chatRoom.id },
      skip: !isStream,
      onData: ({ data }) => {
        const payload = data.data?.chatMembersUpdated;
        if (payload) {
          setOnlineCount(payload.onlineCount);
          setOnlineMembers(payload.members ?? []);
        }
      },
    },
  );

  const messageList = (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <ChatMessageList
        chatRoomId={chatRoom.id}
        mode={mode}
        viewerIsModerator={chatRoom.viewerIsModerator}
        onReply={setReplyTo}
        onMention={setMention}
        stickToBottomRef={stickToBottomRef}
      />
    </Box>
  );

  const body = (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onFocus={() => variant === "mobile" && setIsInputFocused(true)}
      onBlur={() => variant === "mobile" && setIsInputFocused(false)}
    >
      {/* Под стримом форма стоит над лентой, под видео наоборот: заголовок,
          лента, форма внизу. Порядок задаёт ChatHeaderWithForm, поэтому в
          режиме video лента уезжает к нему в children. */}
      <ChatHeaderWithForm
        chatRoomId={chatRoom.id}
        mode={mode}
        viewerBan={viewerBan}
        onlineCount={onlineCount}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        mention={mention}
        onClearMention={() => setMention(null)}
        onMessageSent={() => {
          stickToBottomRef.current = true;
        }}
        onOpenOnlineList={() => setOnlineListOpen(true)}
        onOpenSoundSettings={(anchor) => setSoundAnchor(anchor)}
      >
        {isStream ? null : messageList}
      </ChatHeaderWithForm>
      {isStream ? messageList : null}
      <ChatOnlineList
        open={onlineListOpen}
        onClose={() => setOnlineListOpen(false)}
        members={onlineMembers}
      />
      <ChatSoundSettings
        anchorEl={soundAnchor}
        onClose={() => setSoundAnchor(null)}
      />
    </Box>
  );

  // sidebar рендерится внутри вкладки ContentLayout, которая уже даёт
  // колонку и скролл: своя рамка тут была бы второй. mobile это блок в
  // потоке страницы стрима с разворотом по фокусу (референс Chat.js).
  // page это блок комментариев под видео: та же рамка, но без разворота,
  // потому что под видео поле ввода и так внизу экрана не оказывается.
  if (variant === "sidebar") {
    return body;
  }

  if (variant === "page") {
    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderColor: "divider",
          height: 600,
        }}
      >
        {body}
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderColor: "rgba(255, 255, 255, 0.30)",
        height: isInputFocused ? "calc(100vh - 56.25vw)" : 500,
        position: isInputFocused ? "absolute" : "static",
        top: isInputFocused ? 0 : undefined,
        left: isInputFocused ? 0 : undefined,
        right: isInputFocused ? 0 : undefined,
        zIndex: isInputFocused ? 1300 : undefined,
        transition: "all ease 300ms",
      }}
    >
      {body}
    </Paper>
  );
};
